import { useState, useEffect, useRef, useCallback } from "react";
import { Settings, X, Shield } from "lucide-react";
import { useCookieConsentContext } from "@/hooks/useCookieConsent";
import type { CookiePreferences } from "@/hooks/useCookieConsent";

/**
 * Cookie Banner conforme a:
 * - Provvedimento Garante Privacy 27/02/2025 n. 98 (tre pulsanti pari evidenza)
 * - Linee Guida Garante Privacy 10/06/2021
 * - GDPR Art. 7 (consenso)
 * - DSA Art. 25 (no dark patterns)
 * - EAA / WCAG 2.2 AA (accessibilità)
 */

type View = "banner" | "preferences";

const CookieBanner = () => {
  const { state, acceptAll, rejectAll, savePreferences, resetConsent } =
    useCookieConsentContext();
  const [view, setView] = useState<View>("banner");
  const [localFunctional, setLocalFunctional] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const bannerRef = useRef<HTMLDivElement>(null);
  const firstButtonRef = useRef<HTMLButtonElement>(null);

  // Show banner with slight delay for smooth entrance
  useEffect(() => {
    if (!state.hasInteracted) {
      const timer = setTimeout(() => setIsVisible(true), 300);
      return () => clearTimeout(timer);
    }
  }, [state.hasInteracted]);

  // Focus management: when banner appears, focus the first button
  useEffect(() => {
    if (isVisible && !state.hasInteracted && firstButtonRef.current) {
      firstButtonRef.current.focus();
    }
  }, [isVisible, state.hasInteracted]);

  // Sync local toggle with saved state when opening preferences
  useEffect(() => {
    if (view === "preferences") {
      setLocalFunctional(state.preferences.functional);
    }
  }, [view, state.preferences.functional]);

  // Trap focus inside the banner when visible
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        // Escape = rifiuta (equivale a chiudere senza accettare)
        rejectAll();
        return;
      }
      if (e.key !== "Tab" || !bannerRef.current) return;

      const focusable = bannerRef.current.querySelectorAll<HTMLElement>(
        'button, a[href], input, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [rejectAll]
  );

  const handleSavePreferences = () => {
    const prefs: CookiePreferences = {
      necessary: true,
      functional: localFunctional,
    };
    savePreferences(prefs);
  };

  // ---- Floating button (visible only after interaction) ----
  if (state.hasInteracted) {
    return (
      <button
        onClick={resetConsent}
        className="fixed bottom-4 left-4 z-50 flex items-center justify-center w-12 h-12 rounded-full bg-white border border-border shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
        aria-label="Gestisci preferenze cookie"
        title="Gestisci preferenze cookie"
      >
        <Settings size={20} className="text-primary" />
      </button>
    );
  }

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9998] bg-black/40 backdrop-blur-sm"
        aria-hidden="true"
      />

      {/* Banner */}
      <div
        ref={bannerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Gestione cookie"
        aria-describedby="cookie-banner-description"
        onKeyDown={handleKeyDown}
        className="fixed inset-x-0 bottom-0 z-[9999] animate-fade-up"
        style={{ animationDuration: "0.4s", animationFillMode: "forwards" }}
      >
        <div className="mx-auto max-w-3xl px-4 pb-4 sm:px-6">
          <div className="rounded-2xl bg-white border border-border shadow-2xl overflow-hidden">
            {view === "banner" ? (
              /* ===== VISTA BANNER PRINCIPALE ===== */
              <div className="p-6 sm:p-8">
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                  <Shield size={24} className="text-gold flex-shrink-0" />
                  <h2 className="text-lg font-bold text-primary">
                    La tua privacy è importante
                  </h2>
                </div>

                {/* Description */}
                <p
                  id="cookie-banner-description"
                  className="text-sm text-muted-foreground leading-relaxed mb-6"
                >
                  Questo sito utilizza cookie tecnici necessari per il
                  funzionamento e cookie funzionali (Google Maps) che richiedono
                  il tuo consenso. Puoi accettare, rifiutare o gestire le tue
                  preferenze in qualsiasi momento.{" "}
                  <a
                    href="/cookie-policy"
                    className="text-gold underline hover:text-primary font-medium"
                  >
                    Cookie Policy
                  </a>{" "}
                  &middot;{" "}
                  <a
                    href="/privacy-policy"
                    className="text-gold underline hover:text-primary font-medium"
                  >
                    Privacy Policy
                  </a>
                </p>

                {/*
                  TRE PULSANTI CON PARI EVIDENZA
                  Provvedimento Garante 27/02/2025 n. 98:
                  - Stessa dimensione
                  - Stesso peso tipografico
                  - Colore comparabile (tutti outlined o tutti filled)
                  - Nessun dark pattern (DSA Art. 25)
                */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    ref={firstButtonRef}
                    onClick={acceptAll}
                    className="flex-1 px-6 py-3 rounded-xl text-sm font-semibold border-2 border-primary bg-primary text-white hover:bg-primary/90 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
                  >
                    Accetta Tutti
                  </button>
                  <button
                    onClick={rejectAll}
                    className="flex-1 px-6 py-3 rounded-xl text-sm font-semibold border-2 border-primary bg-primary text-white hover:bg-primary/90 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
                  >
                    Rifiuta Tutti
                  </button>
                  <button
                    onClick={() => setView("preferences")}
                    className="flex-1 px-6 py-3 rounded-xl text-sm font-semibold border-2 border-primary bg-primary text-white hover:bg-primary/90 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
                  >
                    Gestisci Preferenze
                  </button>
                </div>
              </div>
            ) : (
              /* ===== VISTA GESTIONE PREFERENZE ===== */
              <div className="p-6 sm:p-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <Settings size={24} className="text-gold flex-shrink-0" />
                    <h2 className="text-lg font-bold text-primary">
                      Gestisci Preferenze Cookie
                    </h2>
                  </div>
                  <button
                    onClick={() => setView("banner")}
                    className="p-2 rounded-lg hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
                    aria-label="Torna al banner cookie"
                  >
                    <X size={20} className="text-muted-foreground" />
                  </button>
                </div>

                {/* Cookie categories */}
                <div className="space-y-4 mb-6">
                  {/* Necessary - always on */}
                  <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50 border border-border">
                    <div className="flex-1 mr-4">
                      <p className="text-sm font-semibold text-primary">
                        Cookie Tecnici (Necessari)
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Essenziali per il funzionamento del sito. Non possono
                        essere disattivati.
                      </p>
                    </div>
                    <div
                      className="flex-shrink-0 w-12 h-7 rounded-full bg-accent cursor-not-allowed relative"
                      aria-label="Cookie tecnici sempre attivi"
                      role="switch"
                      aria-checked="true"
                      aria-disabled="true"
                    >
                      <div className="absolute right-1 top-1 w-5 h-5 rounded-full bg-white shadow-sm" />
                    </div>
                  </div>

                  {/* Functional */}
                  <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50 border border-border">
                    <div className="flex-1 mr-4">
                      <label
                        htmlFor="cookie-functional"
                        className="text-sm font-semibold text-primary cursor-pointer"
                      >
                        Cookie Funzionali
                      </label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Abilitano funzionalità aggiuntive come la mappa
                        interattiva di Google Maps.
                      </p>
                    </div>
                    <button
                      id="cookie-functional"
                      role="switch"
                      aria-checked={localFunctional}
                      onClick={() => setLocalFunctional(!localFunctional)}
                      className={`flex-shrink-0 w-12 h-7 rounded-full relative transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 ${
                        localFunctional ? "bg-accent" : "bg-gray-300"
                      }`}
                    >
                      <div
                        className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                          localFunctional
                            ? "translate-x-[22px]"
                            : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Save preferences button */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleSavePreferences}
                    className="flex-1 px-6 py-3 rounded-xl text-sm font-semibold border-2 border-primary bg-primary text-white hover:bg-primary/90 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
                  >
                    Salva Preferenze
                  </button>
                  <button
                    onClick={() => setView("banner")}
                    className="flex-1 px-6 py-3 rounded-xl text-sm font-semibold border-2 border-primary text-primary bg-white hover:bg-muted transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
                  >
                    Indietro
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieBanner;
